/**
 * «Удалить текст?» (handoff §9.3). Mounted only while open.
 *
 * Mobile: the §9.3 bottom sheet, an error-fill «Удалить» (52 px) over a
 * secondary «Отмена» (48 px). Desktop: the app's existing confirmation dialog
 * (the shape of the mastery reset and the flow's leave confirm): a centred
 * surface card over a scrim, cancel and the destructive action at the right.
 *
 * The body names the decks whose cards stay: deleting a text never deletes a
 * card (ADR-115 Decision 2). Back (Android) and Esc cancel; focus starts on
 * cancel, the safe choice.
 */
import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useLocale } from '../../i18n/context'
import { useIsDesktop } from '../../hooks/useIsDesktop'
import { useHistorySentinel } from '../../hooks/useHistorySentinel'
import { CONTROL_SURFACE, suppressTouchContextMenu } from '../../utils/controlSurface'

interface Props {
  deckTitles: string[]
  pending: boolean
  error: string | null
  onCancel: () => void
  onConfirm: () => void
}

export default function TextDeleteConfirm({ deckTitles, pending, error, onCancel, onConfirm }: Props) {
  const { tr, tag } = useLocale()
  const t = tr.textList
  // «A», «A» и «B», «A», «B» и «C»: the interface language's own list form.
  const names = new Intl.ListFormat(tag, { type: 'conjunction' }).format(deckTitles.map(t.quote))
  const bodyText = deckTitles.length === 0 ? t.deleteBody
    : deckTitles.length === 1 ? t.deleteBodyOne(names) : t.deleteBodyMany(names)
  const desktop = useIsDesktop()
  const cancelRef = useRef<HTMLButtonElement>(null)
  const onCancelRef = useRef(onCancel)
  useEffect(() => { onCancelRef.current = onCancel })
  // Back closes it, and it joins the top-level swipe block while open (ADR-106 P3).
  useHistorySentinel(true, 'text-delete-confirm', () => onCancelRef.current())

  useEffect(() => {
    cancelRef.current?.focus()
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      e.stopImmediatePropagation()
      onCancelRef.current()
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [])

  const confirmLabel = pending ? t.deleting : t.deleteConfirm
  const title = (
    <h2 id="text-delete-title" style={{ margin: '0 0 8px', fontSize: desktop ? 18 : 20, fontWeight: 700, color: 'var(--text-primary)' }}>
      {t.deleteTitle}
    </h2>
  )
  const body = (
    <>
      <p id="text-delete-body" style={{ margin: 0, fontSize: 14, lineHeight: 1.5, color: 'var(--text-secondary)' }}>
        {bodyText}
      </p>
      {error && <p role="alert" style={{ margin: '12px 0 0', fontSize: 13.5, color: 'var(--danger)' }}>{error}</p>}
    </>
  )
  const danger: React.CSSProperties = {
    background: 'var(--danger-fill)', color: 'var(--text-on-dark)', border: 'none', borderRadius: 'var(--radius-pill)',
    fontFamily: 'var(--font-ui)', fontWeight: 600, cursor: pending ? 'default' : 'pointer', opacity: pending ? 0.6 : 1,
  }
  const secondary: React.CSSProperties = {
    background: 'var(--bg-secondary)', color: 'var(--text-primary)', border: 'none', borderRadius: 'var(--radius-pill)',
    fontFamily: 'var(--font-ui)', fontWeight: 600, cursor: 'pointer',
  }

  const dialogProps = {
    role: 'alertdialog' as const,
    'aria-modal': true,
    'aria-labelledby': 'text-delete-title',
    'aria-describedby': 'text-delete-body',
    'data-testid': 'text-delete-confirm',
    onClick: (e: React.MouseEvent) => e.stopPropagation(),
  }

  const content = desktop ? (
    <div
      onClick={() => onCancelRef.current()}
      className={`add-sheet-scrim ${CONTROL_SURFACE}`}
      onContextMenu={suppressTouchContextMenu}
      style={{ position: 'fixed', inset: 0, zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
    >
      <div
        {...dialogProps}
        style={{
          background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 16,
          boxShadow: 'var(--shadow-float)', padding: 24, maxWidth: 420, width: '100%', fontFamily: 'var(--font-ui)',
        }}
      >
        {title}
        {body}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 24 }}>
          <button ref={cancelRef} type="button" onClick={onCancel} style={{ ...secondary, height: 40, padding: '0 18px', fontSize: 14 }}>
            {t.cancel}
          </button>
          <button type="button" onClick={onConfirm} disabled={pending} style={{ ...danger, height: 40, padding: '0 18px', fontSize: 14 }}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  ) : (
    <div className={CONTROL_SURFACE} onContextMenu={suppressTouchContextMenu} style={{ position: 'fixed', inset: 0, zIndex: 200 }}>
      <div onClick={() => onCancelRef.current()} className="add-sheet-scrim" style={{ position: 'absolute', inset: 0 }} />
      <div
        {...dialogProps}
        style={{
          position: 'absolute', left: 0, right: 0, bottom: 0, background: 'var(--bg-surface)',
          borderRadius: '16px 16px 0 0', boxShadow: 'var(--shadow-float)', fontFamily: 'var(--font-ui)',
          padding: '8px 20px max(20px, env(safe-area-inset-bottom))',
        }}
      >
        <div aria-hidden style={{ width: 36, height: 4, borderRadius: 2, background: 'var(--border)', margin: '0 auto 16px' }} />
        {title}
        {body}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 20 }}>
          <button type="button" onClick={onConfirm} disabled={pending} style={{ ...danger, height: 52, fontSize: 16 }}>
            {confirmLabel}
          </button>
          <button ref={cancelRef} type="button" onClick={onCancel} style={{ ...secondary, height: 48, fontSize: 15 }}>
            {t.cancel}
          </button>
        </div>
      </div>
    </div>
  )
  return createPortal(content, document.body)
}
